import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Button, Card, CardContent, Avatar, Grid,
  Chip, CircularProgress, Divider, Paper, useTheme, Alert
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  CloudUpload as UploadIcon,
  OpenInNew as OpenInNewIcon,
  CheckCircle as CheckIcon,
  Groups as GroupsIcon
} from '@mui/icons-material';
import { MicrosoftIcon as SiMicrosoft } from '../../assets/icons/BrandIcons';
import api from '../../config/api';

const agentColors = {
  'brand': 'var(--color-primary)', 'finance': '#059669', 'retention': '#D97706',
  'ops': '#2563EB', 'media': '#DC2626', 'social': '#EC4899',
  'customer': 'var(--color-accent)', 'amazon': '#F59E0B', 'google-ads': '#4285F4',
  'cro': '#10B981', 'data': '#6B7280'
};

const StepCard = ({ number, title, children, last }) => {
  const theme = useTheme();
  return (
    <Box sx={{ display: 'flex', gap: 2.5, mb: last ? 0 : 0 }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Box sx={{
          width: 36, height: 36, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
          bgcolor: '#6264A7', color: '#fff', fontWeight: 700, fontSize: '0.9rem', flexShrink: 0
        }}>
          {number}
        </Box>
        {!last && (
          <Box sx={{ width: 2, flex: 1, bgcolor: theme.palette.mode === 'dark' ? 'rgba(98,100,167,0.3)' : 'rgba(98,100,167,0.2)', mt: 1 }} />
        )}
      </Box>
      <Box sx={{ flex: 1, pb: last ? 0 : 4 }}>
        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>{title}</Typography>
        {children}
      </Box>
    </Box>
  );
};

const TeamsSetupGuide = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [agentApps, setAgentApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const apiBase = api.defaults.baseURL || '';

  useEffect(() => {
    const fetchAgents = async () => {
      try {
        const res = await api.get('/teams/admin/agent-apps');
        if (res.data?.success) setAgentApps(res.data.data);
      } catch (err) {
        console.error('Failed to fetch agent apps:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchAgents();
  }, []);

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', py: 4, px: 2 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/connectors')}
          sx={{ mb: 2, textTransform: 'none', color: 'text.secondary' }}
        >
          Back to Connectors
        </Button>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
          <Box sx={{
            width: 48, height: 48, borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center',
            bgcolor: '#6264A715', color: '#6264A7'
          }}>
            <SiMicrosoft size={28} />
          </Box>
          <Box>
            <Typography variant="h5" fontWeight={700}>Teams Agent Setup</Typography>
            <Typography variant="body2" color="text.secondary">
              Deploy AI agents to your Microsoft Teams workspace
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* How it works overview */}
      <Card elevation={0} sx={{
        mb: 4, borderRadius: 3, border: '1px solid', borderColor: 'divider',
        background: theme.palette.mode === 'dark'
          ? 'linear-gradient(135deg, rgba(98,100,167,0.08) 0%, rgba(98,100,167,0.02) 100%)'
          : 'linear-gradient(135deg, rgba(98,100,167,0.04) 0%, rgba(255,255,255,1) 100%)'
      }}>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>How it works</Typography>
          <Grid container spacing={2}>
            {[
              { icon: '1', title: 'Admin uploads once', desc: 'Your Teams admin uploads agent app packages to the Teams Admin Center. Takes ~2 minutes for all agents.' },
              { icon: '2', title: 'Available org-wide instantly', desc: 'Every user in the organisation can immediately find and add agents from the Teams Apps sidebar.' },
              { icon: '3', title: 'Users chat with agents', desc: 'Each agent appears as a separate person. Users DM them or @mention in channels to ask questions and run reports.' }
            ].map((item) => (
              <Grid item xs={12} sm={4} key={item.icon}>
                <Box sx={{ textAlign: 'center', p: 1 }}>
                  <Box sx={{
                    width: 40, height: 40, borderRadius: '50%', mx: 'auto', mb: 1.5,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    bgcolor: theme.palette.mode === 'dark' ? 'rgba(98,100,167,0.15)' : 'rgba(98,100,167,0.08)',
                    color: '#6264A7', fontWeight: 700
                  }}>
                    {item.icon}
                  </Box>
                  <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>{item.title}</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.5, display: 'block' }}>{item.desc}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>

      {/* Step-by-step guide */}
      <Typography variant="h6" fontWeight={700} sx={{ mb: 3 }}>Setup Guide</Typography>

      <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', mb: 4 }}>
        <CardContent sx={{ p: 3 }}>
          <StepCard number="1" title="Download the agent manifests">
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Each agent has a manifest package (.zip) containing its identity, icon, and configuration.
              Download all of them at once or individually.
            </Typography>
            <Button
              variant="contained"
              size="small"
              href={`${apiBase}/teams/admin/agent-manifest-all`}
              sx={{
                textTransform: 'none', fontWeight: 600,
                bgcolor: '#6264A7', '&:hover': { bgcolor: '#515399' }
              }}
              startIcon={<UploadIcon sx={{ transform: 'rotate(180deg)' }} />}
            >
              Download All Manifests (.zip)
            </Button>
            {!loading && agentApps.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  Or download individually:
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                  {agentApps.map((agent) => (
                    <Chip
                      key={agent.slug}
                      component="a"
                      href={`${apiBase}${agent.manifestUrl}`}
                      clickable
                      avatar={<Avatar sx={{ bgcolor: agentColors[agent.slug] || 'var(--color-primary)', color: '#fff !important', width: 22, height: 22, fontSize: '0.6rem' }}>{agent.displayName.charAt(0)}</Avatar>}
                      label={agent.displayName.split(' (')[0]}
                      size="small"
                      variant="outlined"
                      sx={{ fontSize: '0.7rem', borderColor: 'divider' }}
                    />
                  ))}
                </Box>
              </Box>
            )}
          </StepCard>

          <StepCard number="2" title="Open the Teams Admin Center">
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              You need to be a Microsoft 365 admin (Global Admin or Teams Admin role).
              This is where you manage apps for your entire organisation.
            </Typography>
            <Button
              variant="outlined"
              size="small"
              href="https://admin.teams.microsoft.com/policies/manage-apps"
              target="_blank"
              rel="noopener noreferrer"
              sx={{ textTransform: 'none', fontWeight: 600 }}
              endIcon={<OpenInNewIcon sx={{ fontSize: '0.9rem !important' }} />}
            >
              Open Teams Admin Center
            </Button>
          </StepCard>

          <StepCard number="3" title="Upload each agent app">
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              In the Teams Admin Center:
            </Typography>
            <Box component="ol" sx={{ pl: 2.5, m: 0, '& li': { mb: 0.75 } }}>
              <Typography component="li" variant="body2" color="text.secondary">
                Navigate to <strong>Teams apps</strong> &rarr; <strong>Manage apps</strong>
              </Typography>
              <Typography component="li" variant="body2" color="text.secondary">
                Click <strong>Upload new app</strong> (top of the page)
              </Typography>
              <Typography component="li" variant="body2" color="text.secondary">
                Select an agent's .zip file and confirm the upload
              </Typography>
              <Typography component="li" variant="body2" color="text.secondary">
                Repeat for each agent you want to enable
              </Typography>
            </Box>
            <Alert severity="info" sx={{ mt: 2, '& .MuiAlert-message': { fontSize: '0.8rem' } }}>
              Each upload takes a few seconds. The app is available to all users in your org immediately after upload.
            </Alert>
          </StepCard>

          <StepCard number="4" title="Users add agents in Teams" last>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Once uploaded, every user in your organisation can add agents themselves:
            </Typography>
            <Box component="ol" sx={{ pl: 2.5, m: 0, '& li': { mb: 0.75 } }}>
              <Typography component="li" variant="body2" color="text.secondary">
                Open <strong>Microsoft Teams</strong> &rarr; click <strong>Apps</strong> in the left sidebar
              </Typography>
              <Typography component="li" variant="body2" color="text.secondary">
                Search for the agent's name (e.g. "Marcus" or "Sarah")
              </Typography>
              <Typography component="li" variant="body2" color="text.secondary">
                Click <strong>Add</strong> &mdash; the agent appears as a chat contact
              </Typography>
              <Typography component="li" variant="body2" color="text.secondary">
                Send a message to start asking questions and running reports
              </Typography>
            </Box>
          </StepCard>
        </CardContent>
      </Card>

      {/* Agent list */}
      <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>Available Agents</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Each agent is a specialist with access to specific data and reports. Users can add any agents relevant to their role.
      </Typography>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={28} />
        </Box>
      ) : (
        <Grid container spacing={1.5}>
          {agentApps.map((agent) => (
            <Grid item xs={12} sm={6} key={agent.slug}>
              <Card elevation={0} sx={{
                border: '1px solid', borderColor: 'divider', borderRadius: 2,
                transition: 'all 0.15s',
                '&:hover': { borderColor: agentColors[agent.slug] || 'var(--color-primary)' }
              }}>
                <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 }, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Avatar sx={{
                    width: 36, height: 36, fontSize: '0.85rem', fontWeight: 700,
                    bgcolor: agentColors[agent.slug] || 'var(--color-primary)', color: '#fff'
                  }}>
                    {agent.displayName.charAt(0)}
                  </Avatar>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" fontWeight={600} noWrap>{agent.displayName}</Typography>
                    <Typography variant="caption" color="text.secondary" noWrap>{agent.role}</Typography>
                  </Box>
                  <Button
                    size="small"
                    variant="text"
                    href={`${apiBase}${agent.manifestUrl}`}
                    sx={{ minWidth: 'auto', px: 1, fontSize: '0.7rem', textTransform: 'none', color: 'text.secondary' }}
                  >
                    .zip
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    href={agent.deepLink}
                    target="_blank"
                    sx={{
                      minWidth: 'auto', px: 2, fontSize: '0.75rem',
                      textTransform: 'none', borderRadius: 1.5
                    }}
                  >
                    Add to Teams
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {agentApps.length > 0 && (
        <Alert severity="success" sx={{ mt: 3, '& .MuiAlert-message': { fontSize: '0.85rem' } }} icon={<CheckIcon />}>
          <strong>"Add to Teams"</strong> links work once the admin has uploaded the corresponding manifest.
          Users who haven't added an agent yet can search for it in Teams &rarr; Apps.
        </Alert>
      )}
    </Box>
  );
};

export default TeamsSetupGuide;
